import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../api/notificationPreferencesService';
import './NotificationSettingsPage.css';

const LABELS = {
  ru: {
    title: 'Email-уведомления',
    loading: 'Загрузка…',
    saveError: 'Не удалось сохранить настройки',
    loadError: 'Не удалось загрузить настройки',
    mandatory: 'обязательно',
    roles: {
      author: 'Автор',
      reviewer: 'Рецензент',
      editor: 'Редактор',
      chief_editor: 'Главный редактор',
    },
    events: {
      festschrift_published: 'Сборник создан',
      festschrift_deadline_changed: 'Изменились даты/дедлайны сборника',
      article_created: 'Я создал(а) статью',
      article_version_uploaded: 'Автор загрузил новую версию статьи',
      article_locked: 'Работа над статьей завершена',
      reviewer_assigned: 'Вам назначена новая статья',
      reviewer_unassigned: 'Вы были сняты с рецензирования статьи',
      review_submitted: 'Рецензия отправлена',
    },

eventsByRole: {
  author: {
    reviewer_assigned: 'Вам назначен новый рецензент',
    reviewer_unassigned: 'Рецензент был снят с вашей статьи',
  },
},
  },
  en: {
    title: 'Email notifications',
    loading: 'Loading…',
    saveError: 'Failed to save preferences',
    loadError: 'Failed to load preferences',
    mandatory: 'required',
    roles: {
      author: 'Author',
      reviewer: 'Reviewer',
      editor: 'Editor',
      chief_editor: 'Chief editor',
    },
    events: {
      festschrift_published: 'Festschrift created',
      festschrift_deadline_changed: 'Festschrift dates/deadlines changed',
      article_created: 'I created an article',
      article_version_uploaded: 'Author uploaded a new article version',
      article_locked: 'Work on the article is complete',
      reviewer_assigned: 'You have been assigned a new article',
      reviewer_unassigned: 'You have been removed from reviewing the article',
      review_submitted: 'Review submitted',
    },

eventsByRole: {
  author: {
    reviewer_assigned: 'A new reviewer has been assigned to you',
    reviewer_unassigned: 'A reviewer has been removed from your article',
  },
},
  },
};

const NotificationSettingsPage = () => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();
  const t = LABELS[lang] || LABELS.en;

  const [data, setData] = useState(null); // { roles: { role: [{event_key, enabled, default_enabled, mandatory}] } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getBreadcrumbs = useCallback(() => {
    return [{ label: t.title }];
  }, [t.title]);

  useUpdateBreadcrumbs(() => getBreadcrumbs());

  const roleOrder = useMemo(() => ['author', 'reviewer', 'editor', 'chief_editor'], []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getNotificationPreferences();
        setData(res);
      } catch (e) {
        console.error(e);
        setError(t.loadError);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [t.loadError]);

  const onToggle = async (role, eventKey) => {
    if (!data?.roles?.[role]) return;

    const items = data.roles[role];
    const idx = items.findIndex((x) => x.event_key === eventKey);
    if (idx === -1) return;

    const item = items[idx];
    if (item.mandatory) return;

    const nextEnabled = !item.enabled;

    // optimistic update
    const next = {
      ...data,
      roles: {
        ...data.roles,
        [role]: items.map((x) => (x.event_key === eventKey ? { ...x, enabled: nextEnabled } : x)),
      },
    };
    setData(next);
    setError('');

    try {
      await updateNotificationPreferences(role, { [eventKey]: nextEnabled });
    } catch (e) {
      console.error(e);
      setError(t.saveError);
      // rollback by refetch (keeps truth from backend)
      try {
        const res = await getNotificationPreferences();
        setData(res);
      } catch (e2) {
        console.error(e2);
      }
    }
  };

  const renderRoleBlock = (role) => {
    const items = data?.roles?.[role];
    if (!items || items.length === 0) return null;

    return (
      <section key={role} className="notif-role">
        <h2 className="notif-role__title">{t.roles[role] || role}</h2>

        <div className="notif-role__list">
          {items.map((item) => {
            const label = (t.eventsByRole?.[role]?.[item.event_key]) || t.events[item.event_key] || item.event_key;

            return (
              <label key={item.event_key} className="notif-item">
                <input
                  type="checkbox"
                  checked={Boolean(item.enabled)}
                  disabled={Boolean(item.mandatory)}
                  onChange={() => onToggle(role, item.event_key)}
                />
                <span className="notif-item__text">
                  {label}
                  {item.mandatory ? (
                    <span className="notif-item__mandatory">{` (${t.mandatory})`}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className="notification-settings">
      <div className="notification-settings__container">
        <h1 className="notification-settings__title">{t.title}</h1>

        {loading ? <div className="notification-settings__loading">{t.loading}</div> : null}
        {error ? <div className="notification-settings__error">{error}</div> : null}

        {!loading && data ? (
          <div className="notification-settings__roles">
            {roleOrder.map((role) => renderRoleBlock(role))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
